import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Card, DatePicker, Input, Modal, Skeleton, Table, Tag, Tooltip, App } from "antd"
import { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useState } from "react"
import * as XLSX from "xlsx"
import { Link, useNavigate } from "react-router-dom"
import { ContainerFull } from "../../components/GeneralComponents"
import { DownloadOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { getInvoices, updateInvoice, cancelInvoice, InvoiceInterface } from "../../endpoints/invoiceEndpoints"

const { RangePicker } = DatePicker

export const Invoices = (): JSX.Element => {
    const { message } = App.useApp()
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // Get invoices from API
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getInvoices"],
        queryFn: getInvoices,
        refetchOnMount: "always",
    })

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return (
            <ContainerFull>
                <Card title="Error">
                    <p>Error loading invoices: {error?.message}</p>
                </Card>
            </ContainerFull>
        )
    }

    const invoices = data?.invoices || []

    // Calculate total received from paid invoices (excluding test invoices 17831 and 17841)
    const getTotalReceived = () => {
        return invoices
            .filter(invoice => invoice.status === 'paid')
            .filter(invoice => invoice.id !== 17831 && invoice.id !== 17841)
            .reduce((total, invoice) => total + (invoice.totalAmount || 0), 0)
    }

    // Calculate total unpaid invoiced amount (excluding test invoices and canceled)
    const getTotalUnpaid = () => {
        return invoices
            .filter(invoice => invoice.status === 'due')
            .filter(invoice => invoice.id !== 17831 && invoice.id !== 17841)
            .reduce((total, invoice) => total + (invoice.totalAmount || 0), 0)
    }

    const columns: ColumnsType<InvoiceInterface> = [
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '4px' }}>
                    <Tooltip title="Download PDF">
                        <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={() => handleDownloadInvoice(record.id)}
                        />
                    </Tooltip>
                    
                    <Tooltip title="Edit Invoice">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => navigate(`/admin/invoice/edit/${record.id}`)}
                        />
                    </Tooltip>
                    
                    <Tooltip title={
                        record.status === 'canceled' ? "Invoice already canceled" :
                        record.status === 'paid' ? "Cannot delete paid invoice" :
                        "Cancel Invoice"
                    }>
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => handleCancelInvoice(record)}
                            disabled={record.status === 'canceled' || record.status === 'paid'}
                        />
                    </Tooltip>
                    
                </div>
            ),
        },
        {
            title: "Invoice ID",
            dataIndex: "id",
            key: "id",
            width: 100,
            render: (id: number) => <Link to={`/admin/invoice/edit/${id}`}><strong>#{id}</strong></Link>,
        },
        {
            title: "Date",
            dataIndex: "billingDate",
            key: "billingDate",
            width: 120,
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : dayjs().format('MMM DD, YYYY'),
            sorter: (a, b) => {
                const dateA = a.billingDate ? dayjs(a.billingDate).unix() : dayjs(a.ts).unix()
                const dateB = b.billingDate ? dayjs(b.billingDate).unix() : dayjs(b.ts).unix()
                return dateA - dateB
            },
        },
        {
            title: "Due Date",
            dataIndex: "dueDate",
            key: "dueDate",
            width: 120,
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-',
            sorter: (a, b) => {
                if (!a.dueDate && !b.dueDate) return 0
                if (!a.dueDate) return 1
                if (!b.dueDate) return -1
                return dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix()
            },
        },
        {
            title: "Orders",
            dataIndex: "orderIds",
            key: "orders",
            width: 120,
            render: (orderIds) => {
                if (!orderIds || orderIds.length === 0) return '-'
                return (
                    <div>
                        {orderIds.slice(0, 2).map((orderId: number, index: number) => (
                            <div key={orderId}>
                                <Link to={`/admin/order/${orderId}`}>#{orderId}</Link>
                            </div>
                        ))}
                        {orderIds.length > 2 && (
                            <Tooltip title={orderIds.slice(2).map((id: number) => `#${id}`).join(', ')}>
                                <span style={{ color: '#666', fontSize: '12px' }}>
                                    +{orderIds.length - 2} more
                                </span>
                            </Tooltip>
                        )}
                    </div>
                )
            },
        },
        {
            title: "Payment Method",
            dataIndex: "paymentMethod",
            key: "paymentMethod",
            width: 120,
            render: (method) => (
                <Tag color={method === 'Credit Card' ? 'blue' : method === 'PO' ? 'green' : 'default'}>
                    {method}
                </Tag>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => {
                const color = status === 'paid' ? 'success' : status === 'canceled' ? 'error' : 'warning'
                const text = status === 'paid' ? 'Paid' : status === 'canceled' ? 'Canceled' : 'Due'
                return (
                    <Tag color={color}>
                        {text}
                    </Tag>
                )
            },
            filters: [
                { text: 'Paid', value: 'paid' },
                { text: 'Due', value: 'due' },
                { text: 'Canceled', value: 'canceled' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: "Subtotal",
            dataIndex: "subTotal",
            key: "subTotal",
            width: 100,
            render: (amount) => `$${(amount || 0).toFixed(2)}`,
            align: 'right',
        },
        {
            title: "Discount",
            dataIndex: "discount",
            key: "discount",
            width: 100,
            render: (amount) => amount ? `-$${amount.toFixed(2)}` : '$0.00',
            align: 'right',
        },
        {
            title: "Total",
            dataIndex: "totalAmount",
            key: "total",
            width: 100,
            render: (amount) => <strong>${(amount || 0).toFixed(2)}</strong>,
            align: 'right',
            sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
        },

        {
            title: "Institution",
            dataIndex: "billingInstitution",
            key: "institution",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (institution) => (
                <Tooltip placement="topLeft" title={institution}>
                    {institution}
                </Tooltip>
            ),
        },
        {
            title: "Contact",
            dataIndex: "billingContactName",
            key: "contact",
            width: 150,
            render: (name) => name || '-',
        },
        {
            title: "Email",
            dataIndex: "billingEmail",
            key: "email",
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            render: (email) => (
                <Tooltip placement="topLeft" title={email}>
                    {email}
                </Tooltip>
            ),
        },
    ]

    const handleDownloadInvoice = async (invoiceId: number) => {
        try {
            const url = `/api/download/invoice/${invoiceId}?r=${Math.random()}`
            window.open(url, '_blank')
        } catch (error) {
            console.error('Error downloading invoice:', error)
            message.error('Failed to download invoice')
        }
    }

    const handleCancelInvoice = async (invoice: InvoiceInterface) => {
        Modal.confirm({
            title: 'Cancel Invoice',
            content: `Are you sure you want to cancel invoice #${invoice.id}? This action cannot be undone and will remove the invoice from all associated orders.`,
            okText: 'Yes, Cancel Invoice',
            okType: 'danger',
            cancelText: 'Keep Invoice',
            onOk: async () => {
                try {
                    const response = await cancelInvoice({
                        invoiceId: invoice.id
                    })
                    
                    if (response.code === 1000) {
                        message.success('Invoice cancelled successfully')
                        queryClient.invalidateQueries({ queryKey: ["getInvoices"] })
                    } else {
                        message.error(response.message || 'Failed to cancel invoice')
                    }
                } catch (error) {
                    console.error('Error cancelling invoice:', error)
                    message.error('Failed to cancel invoice')
                }
            }
        })
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys)
        },
        getCheckboxProps: (record: InvoiceInterface) => ({
            name: record.id.toString(),
        }),
    }

    const handleDownloadSelectedTsv = () => {
        const selectedInvoices = invoices.filter(inv => selectedRowKeys.includes(inv.id))
        if (selectedInvoices.length === 0) {
            message.warning('No invoices selected')
            return
        }

        const rows = selectedInvoices.map(inv => ({
            'Invoice ID': inv.id,
            'Billing Date': inv.billingDate ? new Date(inv.billingDate).toLocaleDateString() : '',
            'Due Date': inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '',
            'Status': inv.status,
            'Institution': inv.billingInstitution,
            'Contact Name': inv.billingContactName,
            'Email': inv.billingEmail,
            'Address Line 1': inv.billingAddressLine1,
            'Address Line 2': inv.billingAddressLine2 ?? '',
            'City': inv.billingCity,
            'State': inv.billingState,
            'Zip': inv.billingZipcode,
            'Country': inv.billingCountry,
            'Payment Method': inv.paymentMethod,
            'Subtotal': inv.subTotal ?? 0,
            'Discount': inv.discount ?? 0,
            'Total Amount': inv.totalAmount ?? 0,
            'Payment Received': inv.payment ?? 0,
            'Notes': inv.notes ?? '',
        }))

        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Invoices')
        XLSX.writeFile(wb, `invoices_${new Date().toISOString().slice(0, 10)}.xlsx`)
        message.success(`Downloaded ${selectedInvoices.length} invoice(s) as Excel`)
    }

    const handleBulkActions = (action: string) => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one invoice')
            return
        }

        switch (action) {
            case 'markPaid':
                handleBulkStatusUpdate('paid')
                break
            case 'markDue':
                handleBulkStatusUpdate('due')
                break
            case 'cancelSelected':
                handleBulkCancelInvoices()
                break
            case 'downloadBatch':
                handleDownloadSelectedTsv()
                break
            default:
                break
        }
    }

    const handleBulkStatusUpdate = async (newStatus: 'paid' | 'due') => {
        const selectedInvoices = invoices.filter(invoice => 
            selectedRowKeys.includes(invoice.id)
        )
        
        if (selectedInvoices.length === 0) {
            message.warning('No invoices selected')
            return
        }

        const statusText = newStatus === 'paid' ? 'paid' : 'due'
        const actionText = newStatus === 'paid' ? 'Mark as Paid' : 'Mark as Due'

        Modal.confirm({
            title: `${actionText}`,
            content: `Are you sure you want to mark ${selectedInvoices.length} invoice(s) as ${statusText}? This will also update the payment status of associated orders.`,
            okText: `Yes, ${actionText}`,
            onOk: async () => {
                message.loading({ content: `Updating ${selectedInvoices.length} invoices...`, key: 'bulkUpdate' })
                
                const promises = selectedInvoices.map(invoice => 
                    updateInvoice({
                        invoiceId: invoice.id,
                        status: newStatus
                    })
                )
                
                try {
                    const results = await Promise.allSettled(promises)
                    const successCount = results.filter(result => 
                        result.status === 'fulfilled' && result.value.code === 1000
                    ).length
                    
                    if (successCount === selectedInvoices.length) {
                        message.success({ content: `Successfully marked ${successCount} invoices as ${statusText}`, key: 'bulkUpdate' })
                    } else {
                        message.warning({ content: `Updated ${successCount} of ${selectedInvoices.length} invoices`, key: 'bulkUpdate' })
                    }
                    
                    setSelectedRowKeys([])
                    queryClient.invalidateQueries({ queryKey: ["getInvoices"] })
                } catch (error) {
                    console.error('Error updating invoices:', error)
                    message.error({ content: 'Failed to update some invoices', key: 'bulkUpdate' })
                }
            }
        })
    }

    const handleBulkCancelInvoices = () => {
        const selectedInvoices = invoices.filter(invoice => 
            selectedRowKeys.includes(invoice.id) && invoice.status !== 'paid' && invoice.status !== 'canceled'
        )
        
        if (selectedInvoices.length === 0) {
            message.warning('No unpaid/due invoices selected for cancellation')
            return
        }

        const nonCancellableCount = selectedRowKeys.length - selectedInvoices.length
        const warningMessage = nonCancellableCount > 0 
            ? `${nonCancellableCount} invoices cannot be cancelled (already paid or canceled). ` 
            : ''

        Modal.confirm({
            title: 'Cancel Selected Invoices',
            content: `${warningMessage}Are you sure you want to cancel ${selectedInvoices.length} unpaid invoice(s)? This action cannot be undone.`,
            okText: 'Yes, Cancel Invoices',
            okType: 'danger',
            cancelText: 'Keep Invoices',
            onOk: async () => {
                const promises = selectedInvoices.map(invoice => 
                    cancelInvoice({ invoiceId: invoice.id })
                )
                
                try {
                    const results = await Promise.allSettled(promises)
                    const successCount = results.filter(result => 
                        result.status === 'fulfilled' && result.value.code === 1000
                    ).length
                    
                    if (successCount === selectedInvoices.length) {
                        message.success(`Successfully cancelled ${successCount} invoices`)
                    } else {
                        message.warning(`Cancelled ${successCount} of ${selectedInvoices.length} invoices`)
                    }
                    
                    setSelectedRowKeys([])
                    queryClient.invalidateQueries({ queryKey: ["getInvoices"] })
                } catch (error) {
                    console.error('Error cancelling invoices:', error)
                    message.error('Failed to cancel some invoices')
                }
            }
        })
    }

    return (
        <ContainerFull>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ marginBottom: 8 }}>Invoices Management</h2>
                    <div style={{ display: 'flex', gap: '24px' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            Total Received: ${getTotalReceived().toFixed(0)}
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            Total Due: ${getTotalUnpaid().toFixed(0)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <RangePicker
                        placeholder={['Start Date', 'End Date']}
                        style={{ width: 240 }}
                        onChange={(dates) => {
                            // TODO: Implement date filtering
                            console.log('Date filter:', dates)
                        }}
                    />
                    <Input.Search
                        placeholder="Search by institution, contact, or email"
                        style={{ width: 300 }}
                        onSearch={(value) => {
                            // TODO: Implement search
                            console.log('Search:', value)
                        }}
                    />
                </div>
            </div>

            <div style={{ marginBottom: 16 }}>
                <Button 
                    disabled={selectedRowKeys.length === 0} 
                    onClick={() => handleBulkActions('markPaid')}
                    style={{ marginRight: 8 }}
                >
                    Mark as Paid
                </Button>
                <Button 
                    disabled={selectedRowKeys.length === 0} 
                    onClick={() => handleBulkActions('markDue')}
                    style={{ marginRight: 8 }}
                >
                    Mark as Due
                </Button>
                <Button 
                    disabled={selectedRowKeys.length === 0} 
                    onClick={() => handleBulkActions('cancelSelected')}
                    style={{ marginRight: 8 }}
                    danger
                >
                    Cancel Selected
                </Button>
                <Button 
                    disabled={selectedRowKeys.length === 0} 
                    onClick={() => handleBulkActions('downloadBatch')}
                >
                    Download Selected
                </Button>
            </div>

            <Table
                dataSource={invoices.map((invoice) => ({ ...invoice, key: invoice.id }))}
                columns={columns}
                rowSelection={rowSelection}
                pagination={{ 
                    position: ["bottomLeft"], 
                    pageSize: 25,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`
                }}
                size="middle"
                scroll={{ x: 1200 }}
            />
        </ContainerFull>
    )
}
